import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, MicrosoftExcelLogo } from "@phosphor-icons/react";
import TableToolbar, { SortableTh } from "@/components/TableToolbar";
import { Button } from "@/components/ui/button";
import { useTableView } from "@/hooks/useTableView";
import { useAuth } from "@/context/AuthContext";

export default function UpcomingList({ kind, title, subtitle, testidPrefix }) {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const { data } = useQuery({
    queryKey: ["upcoming", kind],
    queryFn: async () => (await api.get(`/upcoming?kind=${kind}&days=365`)).data,
  });
  const markPaidTx = useMutation({
    mutationFn: async (id) => (await api.post(`/transactions/${id}/mark-paid`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["upcoming", kind] });
      toast.success("Ödendi olarak işaretlendi");
    },
  });
  const markPaidInv = useMutation({
    mutationFn: async (id) => (await api.post(`/invoices/${id}/mark-paid`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["upcoming", kind] });
      toast.success("Ödendi olarak işaretlendi");
    },
  });
  const onMarkPaid = (t) => {
    if (t.source === "contact") return;
    if (t.source === "invoice") markPaidInv.mutate(t.id);
    else markPaidTx.mutate(t.id);
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/upcoming/export.xlsx?kind=${kind}`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `${kind === "income" ? "tahsilatlar" : "odemeler"}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Excel indirilemedi");
    } finally {
      setDownloading(false);
    }
  };

  const items = data?.items || [];
  const view = useTableView({ dateField: "due_date", amountField: "amount_try" });
  // fall back to date when due_date missing for filter/sort
  const enriched = items.map((t) => ({ ...t, due_date: t.due_date || t.date }));
  const visible = view.apply(enriched);
  const total = visible.reduce((s, x) => s + (x.amount_try || 0), 0);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid={`${testidPrefix}-page`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">{subtitle}</div>
          <h1 className="mt-1 text-3xl font-black tracking-tight">{title}</h1>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={downloadExcel} disabled={downloading} data-testid={`${testidPrefix}-export-excel`}>
              <MicrosoftExcelLogo size={14} className="mr-1" />
              {downloading ? "Hazırlanıyor..." : "Excel İndir"}
            </Button>
          )}
          <div className="rounded-md border border-border bg-white px-4 py-2">
            <div className="overline">Görüntülenen Toplam (TL)</div>
            <div className={`mono text-xl font-semibold ${kind === "income" ? "text-emerald-700" : "text-red-600"}`}>
              {fmtMoney(total)}
            </div>
          </div>
        </div>
      </div>

      <TableToolbar
        filters={view.filters}
        setFilters={view.setFilters}
        onReset={view.resetFilters}
        testidPrefix={testidPrefix}
        showPaymentType={true}
        countInfo={`${visible.length} / ${items.length} kayıt`}
        csvExport={{
          filename: `${kind === "income" ? "tahsilatlar" : "odemeler"}-${new Date().toISOString().slice(0, 10)}.csv`,
          columns: [
            { label: "Vade", value: (t) => (t.source === "contact" ? "" : fmtDate(t.due_date)) },
            { label: "Kaynak", value: (t) => (t.source === "contact" ? "Cari Bakiye" : t.source === "invoice" ? "Fatura" : "İşlem") },
            { label: "Cari", value: (t) => t.contact_name || "" },
            { label: "Kategori", value: (t) => t.category || "" },
            { label: "Ödeme Türü", value: (t) => (t.source === "contact" ? "-" : ((t.payment_type || "resmi") === "resmi" ? "Resmi" : "Açıktan")) },
            { label: "Açıklama", value: (t) => t.description || "" },
            { label: "Tutar", value: (t) => t.amount },
            { label: "Para Birimi", value: (t) => t.currency || "TRY" },
            { label: "TL Karşılığı", value: (t) => t.amount_try },
          ],
          rows: visible,
          totalRow: ["", "", "", "", "", "", "GÖRÜNTÜLENEN TOPLAM (TL)", "", total],
        }}
      />

      <div className="mt-4 overflow-x-auto rounded-md border border-border bg-white">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="border-b border-border bg-secondary/50">
            <tr className="text-xs uppercase text-muted-foreground tracking-wider">
              <SortableTh label="Vade" sortKey="due_date" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid={`${testidPrefix}-sort-date`} />
              <th className="px-4 py-3 text-left">Kaynak</th>
              <th className="px-4 py-3 text-left">Cari</th>
              <SortableTh label="Kategori" sortKey="category" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid={`${testidPrefix}-sort-category`} />
              <SortableTh label="Ödeme Türü" sortKey="payment_type" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid={`${testidPrefix}-sort-paymenttype`} />
              <SortableTh label="Açıklama" sortKey="description" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid={`${testidPrefix}-sort-desc`} />
              <SortableTh label="Tutar" sortKey="amount" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} align="right" testid={`${testidPrefix}-sort-amount`} />
              <SortableTh label="TL Karşılığı" sortKey="amount_try" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} align="right" testid={`${testidPrefix}-sort-amount_try`} />
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={9} className="py-10 text-center text-sm text-muted-foreground">
                {items.length === 0 ? "Bekleyen kayıt yok." : "Filtrelere uyan kayıt yok."}
              </td></tr>
            )}
            {visible.map((t) => {
              const overdue = t.due_date && t.due_date < today && t.source !== "contact";
              const isInvoice = t.source === "invoice";
              const isContact = t.source === "contact";
              const pt = t.payment_type || "resmi";
              return (
                <tr key={`${t.source}-${t.id}`} className="border-b border-border/60 hover:bg-secondary/30">
                  <td className={`px-4 py-3 mono ${overdue ? "text-red-600 font-semibold" : ""}`}>
                    {isContact ? "—" : fmtDate(t.due_date)}
                    {overdue && <span className="ml-2 text-[10px] uppercase tracking-wider">Gecikmiş</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-sm px-2 py-0.5 text-[10px] font-semibold ${
                      isContact ? "bg-emerald-50 text-emerald-700" : isInvoice ? "bg-indigo-50 text-indigo-700" : "bg-secondary text-foreground"
                    }`} data-testid={`${testidPrefix}-source-${t.id}`}>
                      {isContact ? "Cari Bakiye" : isInvoice ? "Fatura" : "İşlem"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{t.contact_name || "-"}</td>
                  <td className="px-4 py-3">{t.category || "-"}</td>
                  <td className="px-4 py-3">
                    {isContact ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <span className={`inline-block rounded-sm px-2 py-0.5 text-[10px] font-semibold ${
                        pt === "resmi" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"
                      }`} data-testid={`${testidPrefix}-paymenttype-${t.id}`}>
                        {pt === "resmi" ? "Resmi" : "Açıktan"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{t.description || "-"}</td>
                  <td className="px-4 py-3 mono text-right">{fmtMoney(t.amount, t.currency)}</td>
                  <td className="px-4 py-3 mono text-right text-muted-foreground">{fmtMoney(t.amount_try, "TRY")}</td>
                  <td className="px-4 py-3 text-right">
                    {isContact ? (
                      <span className="text-[10px] uppercase text-muted-foreground">Bakiye</span>
                    ) : isAdmin && (
                      <button
                        onClick={() => onMarkPaid(t)}
                        data-testid={`${testidPrefix}-markpaid-${t.id}`}
                        className="rounded border border-border px-2 py-1 text-xs hover:bg-secondary"
                      >
                        <CheckCircle size={12} className="mr-1 inline" /> {kind === "income" ? "Tahsil Edildi" : "Ödendi"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {visible.length > 0 && (
            <tfoot className="border-t-2 border-border bg-secondary/40">
              <tr>
                <td colSpan={7} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Görüntülenen Toplam (TL)
                </td>
                <td
                  className={`px-4 py-3 mono text-right text-base font-black ${
                    kind === "income" ? "text-emerald-700" : "text-red-600"
                  }`}
                  data-testid={`${testidPrefix}-total-try`}
                >
                  {fmtMoney(total)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
